import React, { useState, useMemo, useEffect } from "react";
import { SelectTagWidgetContainer } from "../../BroadcastView.styles";
import {
  BroadcastAppCard,
  Container,
  ContainerActions,
  SelectTemplateBar,
  SelectTemplateContainer,
  SelectTemplateHeader,
} from "../styles";
import { BackLink } from "../../Campaigns.style";
import Button from "../../../../../components/shared/components/button/Button.style";
import { useSelector } from "react-redux";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useGetWhatsappTemplateByChannel } from "../api/queries/use-get-whatsapp-template-by-channel";
import { toast } from "react-toastify";
import {
  SearchInputWrapper,
  SearchIcon,
  SearchInput,
  RefetchSpinner,
  TemplateGrid,
  PaginationContainer,
  PageButton,
  EmptyStateContainer,
  EmptyStateTitle,
  EmptyStateText,
  EmptyStateIcon,
} from "../styles";
import { TemplatePreviewSkeleton } from "./template-preview-skeleton";
import { TemplatePreview } from "./template-preview";

const ITEMS_PER_PAGE = 6;

export const TemplateSelectForm = ({
  handleNext,
  handleBack,
  handleSkip,
  updateBroadcastData,
  broadcastData,
}) => {
  const {
    value: name,
    setValue: setName,
    debouncedValue: nameValue,
  } = useDebounce("");

  const channelId = useSelector((state) => state.campaign.create.channelId);

  const {
    data: allTemplates,
    isLoading,
    isFetching,
  } = useGetWhatsappTemplateByChannel(channelId, nameValue);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(
    broadcastData?.selectedTemplate || null,
  );

  console.log({ selectedTemplate });

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [nameValue]);

  // Client-side pagination logic
  const totalPages = allTemplates
    ? Math.ceil(allTemplates.length / ITEMS_PER_PAGE)
    : 0;

  const currentData = useMemo(() => {
    if (!allTemplates) return [];

    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;

    return allTemplates.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [allTemplates, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleContinue = () => {
    if (!selectedTemplate) {
      toast.error("Por favor selecciona una plantilla para continuar.");
      return;
    }

    updateBroadcastData({
      templateId: selectedTemplate.id,
      templateName: selectedTemplate.name,
      templateCategory: selectedTemplate.category,
      bodyMessage: selectedTemplate.components.body,
      footerMessage: selectedTemplate.components.footer,
      templateVariables: selectedTemplate.components.variables,
      buttons: selectedTemplate.components.buttons,
      bodyMessageWithFormat: selectedTemplate.components.bodyFormatted,
      templateFileUrl: selectedTemplate.components.headerUrl,
      templateFileType: selectedTemplate.components.fileType,
    });

    if (
      !selectedTemplate.components.buttons ||
      selectedTemplate.components.buttons?.length === 0
    ) {
      handleSkip();
      return;
    }

    handleNext();
  };

  return (
    <SelectTagWidgetContainer>
      <BroadcastAppCard>
        <Container>
          <SelectTemplateContainer>
            <SelectTemplateHeader>
              <SelectTemplateBar>
                <SearchInputWrapper>
                  <SearchIcon
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </SearchIcon>

                  <SearchInput
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Buscar por nombre..."
                  />
                </SearchInputWrapper>

                {isFetching && !isLoading && <RefetchSpinner />}
              </SelectTemplateBar>
            </SelectTemplateHeader>

            {isLoading && (
              <TemplateGrid>
                {Array.from({ length: 6 }).map((_, i) => (
                  <TemplatePreviewSkeleton key={i} />
                ))}
              </TemplateGrid>
            )}

            {!isLoading && currentData.length > 0 && (
              <>
                <TemplateGrid>
                  {currentData.map((template) => (
                    <TemplatePreview
                      key={template.id}
                      template={template}
                      selectedTemplate={selectedTemplate}
                      setSelectedTemplate={setSelectedTemplate}
                    />
                  ))}
                </TemplateGrid>

                {totalPages > 1 && (
                  <PaginationContainer>
                    <PageButton
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      &lt;
                    </PageButton>

                    {Array.from({ length: totalPages }).map((_, i) => {
                      const page = i + 1;
                      // Easy pagination ellipsis logic could go here; keeping it simple:
                      if (
                        totalPages > 7 &&
                        page !== 1 &&
                        page !== totalPages &&
                        Math.abs(currentPage - page) > 1
                      ) {
                        if (page === 2 || page === totalPages - 1)
                          return <span key={page}>...</span>;
                        return null;
                      }
                      return (
                        <PageButton
                          key={page}
                          $active={currentPage === page}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </PageButton>
                      );
                    })}

                    <PageButton
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      &gt;
                    </PageButton>
                  </PaginationContainer>
                )}
              </>
            )}

            {!isLoading && currentData.length === 0 && (
              <EmptyStateContainer>
                <EmptyStateIcon>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                </EmptyStateIcon>

                <EmptyStateTitle>No se encontraron plantillas</EmptyStateTitle>

                <EmptyStateText>
                  No tienes plantillas que coincidan con "{name}". Intenta
                  buscar con otro término.
                </EmptyStateText>
              </EmptyStateContainer>
            )}
          </SelectTemplateContainer>
        </Container>

        <ContainerActions>
          <BackLink onClick={handleBack}>← Atrás</BackLink>

          <Button
            designV4
            type="button"
            disabled={!selectedTemplate}
            onClick={handleContinue}
          >
            Continuar
          </Button>
        </ContainerActions>
      </BroadcastAppCard>
    </SelectTagWidgetContainer>
  );
};
